import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { useSelector } from 'react-redux';

import { BookingProcessModal } from '@components/BookingProcessModal';
import {
  useModalVisibility,
  UseModalVisibilityHookReturn,
} from '@hooks/shared/useModalVisibility';
import { RootState } from '@lib/redux.lib/store';

type ContextValue = {
  bookingProcessModalVisibility: UseModalVisibilityHookReturn;
  bookingProcessTutorialExperienceTrigger: BookingProcessTutorialExperienceTriggerType;
  setBookingProcessTutorialExperienceTrigger: ReactStateUpdater<BookingProcessTutorialExperienceTriggerType>;
  markBookingProcessTutorialHandled: () => void;
};

const TutorialContext = createContext({} as ContextValue);

export type BookingProcessTutorialExperienceTriggerType =
  | 'automatic'
  | 'manual';

const TutorialContextProvider = ({ children }: { children: ReactNode }) => {
  const [
    bookingProcessTutorialExperienceTrigger,
    setBookingProcessTutorialExperienceTrigger,
  ] = useState<BookingProcessTutorialExperienceTriggerType>('automatic');
  const bookingProcessModalVisibility = useModalVisibility();

  const showBookingModalHasBeenTriggered = useRef(false);
  const markBookingProcessTutorialHandled = useCallback(() => {
    showBookingModalHasBeenTriggered.current = true;
  }, []);

  const userProfile = useSelector(
    (state: RootState) => state.app.user.IUserInfoDAO
  );

  const userTutorials = useMemo(
    () => userProfile?.user_tutorials ?? [],
    [userProfile?.user_tutorials]
  );
  const bookingProcessTutorial = useMemo(
    () =>
      userTutorials.find(
        ({ tutorial_name }) => tutorial_name === 'booking_process'
      ),
    [userTutorials]
  );

  useEffect(() => {
    const tutorialStillRequired =
      !showBookingModalHasBeenTriggered.current &&
      !bookingProcessTutorial?.is_completed &&
      userProfile?.bg_onboarded === true;

    if (!tutorialStillRequired) return;

    if (
      bookingProcessModalVisibility.isVisible &&
      bookingProcessTutorialExperienceTrigger === 'manual'
    ) {
      return;
    }

    flushSync(() => {
      setBookingProcessTutorialExperienceTrigger('automatic');
    });
    bookingProcessModalVisibility.showModal();
    markBookingProcessTutorialHandled();
  }, [
    bookingProcessTutorialExperienceTrigger,
    bookingProcessTutorial?.is_completed,
    userProfile?.bg_onboarded,
    bookingProcessModalVisibility,
    markBookingProcessTutorialHandled,
  ]);

  const providerValue = useMemo(
    () => ({
      bookingProcessModalVisibility,
      bookingProcessTutorialExperienceTrigger,
      setBookingProcessTutorialExperienceTrigger,
      markBookingProcessTutorialHandled,
    }),
    [
      bookingProcessModalVisibility,
      bookingProcessTutorialExperienceTrigger,
      markBookingProcessTutorialHandled,
    ]
  );

  return (
    <TutorialContext.Provider value={providerValue}>
      <BookingProcessModal />
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorialContext: () => ContextValue = () =>
  useContext<ContextValue>(TutorialContext);

export { TutorialContextProvider };
